import { keepPreviousData, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ContentInboxItemDTO, ContentVersionDTO } from '@today/types'
import type { ApproveDialogViewProps, InboxRowView, InboxViewProps, ReasonDialogViewProps } from '@today/ui-domain'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { contentService } from '@/services/content.service'
import { day } from './content-ui'
import { isInForce } from './text-list-rules'
import { actorLabel } from './track-rules'
import { contentKeys, useContentT, useContentViewer, useNow } from './useContentQueries'
import { useReviewDialogs } from './useReviewDialogs'

/** `/content/texts/{id}?version=N&locale=L&compare=M`, where vM holds the in-force localization; null if none. */
export const compareHref = (
	item: ContentInboxItemDTO,
	versions: readonly ContentVersionDTO[] | undefined,
	now: Date,
) => {
	const inForce = versions?.find((v) => v.localizations.some((l) => l.locale === item.locale && isInForce(l, now)))
	if (!inForce) return null
	const params = new URLSearchParams({
		version: String(item.versionNumber),
		locale: item.locale,
		compare: String(inForce.versionNumber),
	})
	return `/content/texts/${item.textId}?${params.toString()}`
}

/** One inbox row for the view. */
const inboxRow = (item: ContentInboxItemDTO, viewerId: string, href: string | null): InboxRowView => {
	const author = actorLabel(item.authoredBy, viewerId)
	return {
		localizationId: item.localizationId,
		textCode: item.textCode,
		title: item.title,
		locale: item.locale,
		versionNumber: item.versionNumber,
		authorYou: author.you,
		authorShort: author.short,
		submittedAt: day(item.submittedAt),
		compareHref: href,
	}
}

/** The approval inbox (TO-420 content-admin-ui REQ-008): approve/reject in place, then reload. */
export function useInbox(): { view: InboxViewProps; approve: ApproveDialogViewProps; reason: ReasonDialogViewProps } {
	const { t } = useContentT()
	const navigate = useNavigate()
	const client = useQueryClient()
	const { userId } = useContentViewer()
	const now = useNow()
	const [page, setPage] = useState(1)
	const inbox = useQuery({
		queryKey: [...contentKeys.inbox, page] as const,
		queryFn: () => contentService.inbox({ page: String(page), limit: '20' }),
		placeholderData: keepPreviousData,
	})
	const rows = inbox.data?.data ?? []
	const textIds = [...new Set(rows.map((r) => r.textId))]
	const versions = useQueries({
		queries: textIds.map((id) => ({ queryKey: contentKeys.versions(id), queryFn: () => contentService.versions(id) })),
	})
	const versionsOf = (textId: string) => versions[textIds.indexOf(textId)]?.data
	const review = useReviewDialogs(() =>
		Promise.all([
			client.invalidateQueries({ queryKey: contentKeys.inbox }),
			client.invalidateQueries({ queryKey: contentKeys.texts }),
		]),
	)
	return {
		view: {
			t,
			rows: rows.map((item) => inboxRow(item, userId, compareHref(item, versionsOf(item.textId), now))),
			page,
			pageCount: inbox.data?.totalPages ?? 0,
			empty: inbox.isSuccess && rows.length === 0,
			error: inbox.isError,
			isBusy: review.approve.isBusy === true,
			onApprove: review.openApprove,
			onReject: (id) => review.openReason(id, 'reject'),
			onNavigate: (href) => void navigate(href),
			onPageChange: setPage,
			onRetry: () => void inbox.refetch(),
		},
		approve: review.approve,
		reason: review.reason,
	}
}
